import { type FC, useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  type ModalOverlayProps,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { BufferWhileExiting } from "../helpers/bufferWhileExiting.js";
import { Size } from "../helpers/helpers.js";
import { ModalBackdrop } from "../Modal/modalBackdrop.js";
import { modalOverlay } from "../Modal/modalOverlay.js";

const sizeToMaxWidth: Record<Size, string> = {
  [Size.SMALL]: "max-w-lg",
  [Size.MEDIUM]: "max-w-3xl",
  [Size.LARGE]: "max-w-6xl",
};

export interface SlideoverContainerProps extends Omit<
  ModalOverlayProps,
  "className"
> {
  size?: Size;
}

/** Container that presents a single <Dialog> as a slideover. */
export const SlideoverContainer: FC<SlideoverContainerProps> = (props) => {
  const { size = Size.LARGE, ...modalOverlayProps } = props;
  const [delayedIsOpen, setDelayedIsOpen] = useState(false);
  const isOpen = Boolean(props.isOpen);

  // Delay opening the modal so stacked modals open in the right order (parent then child).
  // TODO: Figure out why this is necessary.
  useEffect(() => {
    setDelayedIsOpen(isOpen);
  }, [isOpen]);

  // TODO: Handle the top bar safe area inset in a better way
  return (
    <ModalOverlay
      {...modalOverlayProps}
      // Don't delay closing to avoid breaking <BufferWhileExiting>
      isOpen={isOpen && delayedIsOpen}
      className={modalOverlay()}
    >
      <ModalBackdrop />
      <div className="absolute inset-0 flex items-stretch justify-end">
        <Modal
          className={twMerge(
            "w-full bg-white shadow-xl transition data-[entering]:translate-x-full data-[exiting]:translate-x-full",
            sizeToMaxWidth[size],
          )}
          data-ar-slideover=""
        >
          {(modalProps) => (
            <BufferWhileExiting
              isOpen={modalProps.state.isOpen}
              isExiting={modalProps.isExiting}
            >
              {typeof props.children === "function"
                ? props.children(modalProps)
                : props.children}
            </BufferWhileExiting>
          )}
        </Modal>
      </div>
    </ModalOverlay>
  );
};
